import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';

import { BehaviorSubject, combineLatest, EMPTY, Observable, of } from 'rxjs';
import { catchError, debounceTime, map, switchMap, take, takeUntil } from 'rxjs/operators';

import { LanguageModule } from '@language';
import { cloneDeep } from 'lodash-es';
import { ApiQuery, Restriction } from '../../../core/api/entities/api-query.entity';
import { AutocompleteRequest } from '../../../core/autocomplete/entities/autocomplete-request.entity';
import { AutocompleteSuggestion } from '../../../core/autocomplete/entities/autocomplete-suggestion.entity';
import { ShAutocompleteService } from '../../../core/autocomplete/services/autocomplete.service';
import { BaseContainer } from '../../../core/common/containers/base.abstract.container';
import { AutocompleteFunction } from '../../../core/common/entities/autocompleteFunction.entity';
import { IsLaxDateRangeQuery } from '../../../core/dates/helpers/date-range-query-helper';
import {
    BaseFieldDefinition,
    EnumValue,
    FieldType
} from '../../../core/field-definitions/entities/field-definition.entity';
import { DottedKeyToFlatKey } from '../../../core/field-definitions/helpers/dottedkey-to-flatkey.helper';
import { ShFieldDefinitionService } from '../../../core/field-definitions/services/field-definition.service';
import { ExtendedFacetProfileField } from '../../../core/profiles/entities/extended-profile-field.entity';
import { EmptyStructuredQuery } from '../../../core/query/entities/empty-structured-query.constant';
import { LogicalOperand, Operand } from '../../../core/query/entities/operand.entity';
import { StructuredQuery } from '../../../core/query/entities/structured-query.entity';
import { DialogConfig } from '../../../dynamic-dialog/entities/dialog-config.entity';
import { DialogRef } from '../../../dynamic-dialog/entities/dialog-ref.class';
import { ShFacetPlaceHolderComponent } from '../../../elements/components/facet-placeholder/facet-placeholder.component';
import {
    ShFacet,
    ShSelectableFacetValue,
    ShSelectedFacetValue
} from '../../../elements/components/facet/entities/facet.entity';
import { ShFacetComponent } from '../../../elements/components/facet/facet.component';
import { NotificationType } from '../../../elements/components/notification/entities/notification-types.entity';
import { ShNotificationComponent } from '../../../elements/components/notification/notification.component';
import { ShFormTextInputComponent } from '../../../form-controls/components/form-text/main/form-text.component';
import { FormSelectOption } from '../../../form-controls/entities/form-select-option.entity';
import { MultiFormSelectOptions } from '../../../form-controls/entities/multi-form-select-options.entity';
import { multiFormSelectOptionWrapper } from '../../../form-controls/helpers/multi-form-select-options-wrapper-helper';
import { LoadingBehaviorSubject } from '../../../helpers/loading-behavior-subject.helper';
import { ShFacetDataService } from '../../data-services/facet.data-service';
import { FacetSelectionType, FacetValueConstraint } from '../../entities/facet-request.entity';
import {
    ProfileFacetSearchDialogConfig,
    ProfileFacetSearchDialogOutput,
    ProfileFacetSearchDialogOutputSuccess
} from '../../entities/facet-search-dialog.entity';
import { ShFacetValueTranslationService } from '../../services/facet-value-translation.service';

function isNotNullOrUndefined<T>(t: T | null | undefined): t is T {
    return t != null;
}

@Component({
    standalone: true,
    selector: 'sh-facet-search-dialog',
    templateUrl: './profile-facet-search-dialog.component.html',
    styleUrls: ['./profile-facet-search-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        LanguageModule,
        ShFacetComponent,
        ShNotificationComponent,
        ShFacetPlaceHolderComponent,
        ShFormTextInputComponent
    ],
    providers: []
})
export class ShProfileFacetSearchDialogComponent extends BaseContainer implements OnInit, OnDestroy {
    protected readonly FACET_FIELD: ExtendedFacetProfileField;
    protected readonly BASE_STRUCTURED_QUERY: StructuredQuery;
    protected readonly QUERY_RESTRICTIONS: Restriction | undefined;
    protected readonly INITIAL_SELECTED_FACETS: ShSelectedFacetValue[];

    protected readonly INCLUDE_ACTIVE_VALUES: boolean = true; // Always return the active values (for count)

    public readonly SUBMIT_LABEL: string;
    public readonly CANCEL_LABEL: string;

    public notificationType: typeof NotificationType = NotificationType;

    public form!: FormGroup;
    public autocompleteFunction!: AutocompleteFunction<ShSelectableFacetValue>;
    public autocompleteErrorMsg: string = '';

    public facetsLoading$: LoadingBehaviorSubject = new LoadingBehaviorSubject();
    public facetError?: boolean;

    // State
    protected readonly COUNT_MAP: Map<string, number> = new Map();

    protected currentPage: number;
    protected selectedState: ShSelectableFacetValue[] = [];
    public facet: ShFacet;

    // Search properties
    public showSearch: boolean;

    protected readonly valueFilterSubject$ = new BehaviorSubject<string>('');
    protected searchResult$: Observable<MultiFormSelectOptions<ShSelectableFacetValue>>;

    // Injectors
    protected readonly fieldDefinitionsService: ShFieldDefinitionService = inject(ShFieldDefinitionService);
    protected readonly facetTranslator: ShFacetValueTranslationService = inject(ShFacetValueTranslationService);
    protected readonly facetsService: ShFacetDataService = inject(ShFacetDataService);
    protected readonly autocompleteService: ShAutocompleteService = inject(ShAutocompleteService);

    constructor(
        protected readonly formBuilder: FormBuilder,
        protected readonly config: DialogConfig<ProfileFacetSearchDialogConfig>,
        protected readonly dialog: DialogRef<ProfileFacetSearchDialogOutput>
    ) {
        super();

        if (!this.config.data) {
            throw new Error('No config provided for dialog');
        }

        this.FACET_FIELD = this.config.data.facetProfileField;

        this.SUBMIT_LABEL = this.config.data.apply;
        this.CANCEL_LABEL = this.config.data.cancel;

        // Strip away current facet from query
        this.BASE_STRUCTURED_QUERY = this.config.data?.query
            ? this.stripFacetFromStructuredQuery(this.config.data?.query.structuredQuery, this.FACET_FIELD.DottedKey)
            : EmptyStructuredQuery;

        this.QUERY_RESTRICTIONS = this.config.data?.query?.restriction;
        this.INITIAL_SELECTED_FACETS = this.config.data?.selectedFacets ?? [];

        // Load 2 pages on init
        this.currentPage = 2;
    }

    ngOnInit(): void {
        this.initSelectedFacetValues();
        this.initFirstResults();

        this.initAutoCompleteFunction();
        this.initForm();
        this.initSearchResult();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();

        this.valueFilterSubject$.complete();
        this.facetsLoading$.complete();
    }

    @HostListener('document:keydown.enter', ['$event'])
    handleEnter(event: { target: HTMLInputElement }) {
        this.submit();
    }

    private initSelectedFacetValues(): void {
        const selectedValuesFromConfig: ShSelectedFacetValue[] = this.INITIAL_SELECTED_FACETS.filter(
            (selected: ShSelectedFacetValue) => this.FACET_FIELD.DottedKey === selected.key
        );

        const initialSelectedValues: ShSelectableFacetValue[] = selectedValuesFromConfig.map(
            (option: ShSelectedFacetValue) =>
                this.toSelectableFacetValue(
                    option.value,
                    option.valueLabel,
                    this.FACET_FIELD.SelectionType,
                    undefined,
                    option.missing,
                    true
                )
        );

        this.setSelectedFacetValues(initialSelectedValues);
    }

    private initFirstResults(): void {
        this.facetsLoading$.startLoading();

        // Get first two pages
        this.loadFacetPages(this.currentPage, true);
    }

    // #region Load and set Facet values
    private loadFacetPages(newPageNumber: number, refreshAllPages?: boolean): void {
        const newPageIndex: number = newPageNumber - 1;
        let pageIndex: number = refreshAllPages ? 0 : newPageIndex;

        const pageCalls = [];

        // Depending on refresh gets all pages or just latest page
        for (pageIndex; pageIndex <= newPageIndex; pageIndex++) {
            pageCalls.push(this.getFacetPage(pageIndex));
        }

        combineLatest(pageCalls)
            .pipe(
                catchError(error => {
                    this.setError(error);
                    return EMPTY;
                }),
                takeUntil(this.destroy$),
                take(1)
            )
            .subscribe(pages => {
                if (refreshAllPages) {
                    this.clearCurrentFacetValues();
                }

                this.addFacetPages(pages);

                this.setSearch();
            });
    }

    protected getFacetPage(pageIndex: number): Observable<ShFacet> {
        const constraint: FacetValueConstraint = {
            StartIndex: pageIndex * this.FACET_FIELD.DefaultNumberOfValues
        };

        return this.facetsService
            .getProfileFieldFacet(this.apiQuery, this.FACET_FIELD, constraint, this.INCLUDE_ACTIVE_VALUES)
            .pipe(take(1));
    }

    private clearCurrentFacetValues(): void {
        this.facet = {
            ...this.facet,
            values: []
        };
    }

    // #endregion

    // #region Autocomplete
    private initAutoCompleteFunction(): void {
        this.fieldDefinitionsService
            .getByDottedKey(this.FACET_FIELD.DottedKey)
            .pipe(
                take(1),
                takeUntil(this.destroy$),
                catchError(error => {
                    console.error('Error fetching field definition:', error);
                    this.autocompleteErrorMsg = 'Failed to load field definitions';
                    return EMPTY;
                })
            )
            .subscribe(fieldDefinition => {
                // ED-4333: Search in translated Enum values
                if (fieldDefinition?.Type === FieldType.EnumField) {
                    const enumAutoCompleteOptions: FormSelectOption<ShSelectableFacetValue>[] =
                        this.enumValuesToSelectableFacetOptions(fieldDefinition);

                    this.autocompleteFunction = (
                        valueFilter: string
                    ): Observable<MultiFormSelectOptions<ShSelectableFacetValue>> =>
                        this.filterEnumValues(valueFilter, enumAutoCompleteOptions);
                } else {
                    this.autocompleteFunction = (
                        valueFilter: string
                    ): Observable<MultiFormSelectOptions<ShSelectableFacetValue>> =>
                        this.queryToAutocomplete(valueFilter);
                }
            });
    }

    private initSearchResult() {
        this.searchResult$ = this.valueFilterSubject$.pipe(
            // MED-4283: prevent too frequent calling facet API
            debounceTime(500),
            switchMap(valueFilter => this.searchFacetValues(valueFilter))
        );
    }

    private queryToAutocomplete(valueFilter: string): Observable<MultiFormSelectOptions<ShSelectableFacetValue>> {
        this.valueFilterSubject$.next(valueFilter);
        return this.searchResult$;
    }

    private searchFacetValues(valueFilter: string): Observable<MultiFormSelectOptions<ShSelectableFacetValue>> {
        if (!valueFilter) {
            return of({
                isLiveData: false,
                options: []
            });
        }

        const request: AutocompleteRequest = this.getAutocompleteRequest(valueFilter);

        return this.autocompleteService.requestAutocompleteSuggestions(request).pipe(
            map((values: AutocompleteSuggestion[]) =>
                values.map(value => {
                    const facetValue = this.mapAutocompleteToShSelectableFacetValue(value);
                    return {
                        label: facetValue.label,
                        value: facetValue
                    };
                })
            ),
            map((formSelectOptions: FormSelectOption<ShSelectableFacetValue>[]) =>
                multiFormSelectOptionWrapper(formSelectOptions)
            ),
            catchError(error => {
                console.error('Error fetching values:', error);
                this.autocompleteErrorMsg = 'Failed to load autocomplete suggestions';
                return EMPTY;
            }),
            take(1)
        );
    }

    protected getAutocompleteRequest(valueFilter: string): AutocompleteRequest {
        return {
            Input: valueFilter,
            Field: this.FACET_FIELD.DottedKey,
            Search: {
                StructuredQuery: this.apiQuery.toApiStructuredQuery()
            }
        };
    }

    private mapAutocompleteToShSelectableFacetValue(value: AutocompleteSuggestion): ShSelectableFacetValue {
        return this.toSelectableFacetValue(
            value.Term,
            this.facetTranslator.translateValue(this.FACET_FIELD, value.Term, false),
            this.facet.selectionType
        );
    }

    private filterEnumValues(
        valueFilter: string,
        options: FormSelectOption<ShSelectableFacetValue>[]
    ): Observable<MultiFormSelectOptions<ShSelectableFacetValue>> {
        return of(options).pipe(
            map(options => options.filter(option => option.label.toLowerCase().includes(valueFilter.toLowerCase()))),
            map(options => multiFormSelectOptionWrapper(options))
        );
    }
    // #endregion

    // #region Form
    private initForm(): void {
        this.form = this.formBuilder.group({
            facetsSearch: []
        });
    }

    get facetsSearchControl(): FormControl {
        return this.form.get('facetsSearch') as FormControl;
    }
    // #endregion

    // #region Add page results to facet to render
    private addFacetPages(facetPages: ShFacet[]) {
        // Sometimes empty pages are fetched resulting in undefined. Omit these.
        facetPages = facetPages.filter(facetPage => !!facetPage);

        if (facetPages.length > 0) {
            this.setFacetResults(facetPages);
            this.orderFacetResults();

            this.clearError();
        }
        this.facetsLoading$.stopLoading();
    }

    private setFacetResults(facetPages: ShFacet[] = []) {
        const mergedFacetValues = this.retainLatestSelectedValues(
            this.facet?.values ?? [],
            facetPages.flatMap(page => page.values)
        );

        // Update current selected facet values with processed API data
        this.setSelectedFacetValues(mergedFacetValues);

        const lastPage = facetPages[facetPages.length - 1];

        this.facet = {
            ...lastPage,
            values: mergedFacetValues
        };
    }

    private retainLatestSelectedValues(
        currentFacetValues: ShSelectableFacetValue[],
        newFacetValues: ShSelectableFacetValue[]
    ): ShSelectableFacetValue[] {
        const selectedValues: string[] = this.selectedState.map(selected => selected.value);

        const markedNewSelectedFacetValues: ShSelectableFacetValue[] = newFacetValues.map(facetValue => {
            const isSelectedValue = selectedValues.includes(facetValue.value);
            return {
                ...facetValue,
                selected: isSelectedValue
            };
        });

        // Use a Map to retain only the last occurrence of each value
        const uniqueFacetValuesMap = new Map<string, ShSelectableFacetValue>();
        [...this.selectedState, ...currentFacetValues, ...markedNewSelectedFacetValues].forEach(facetValue => {
            uniqueFacetValuesMap.set(facetValue.value, facetValue);
        });

        // Convert the Map back to an array
        return Array.from(uniqueFacetValuesMap.values());
    }

    /**
     * Orders current facet values by moving selected facets to the top and 'missing' value to the bottom
     * @returns
     */
    private orderFacetResults() {
        // Mark selected values
        const selectedValues: ShSelectableFacetValue[] = this.selectedState;

        const enhancedFacetValues: ShSelectableFacetValue[] = this.facet.values.map(facetValue => {
            const isSelected = !!selectedValues.find(selectedValue => selectedValue.value === facetValue.value);

            return {
                ...facetValue,
                selected: isSelected
            };
        });

        // Manually add selected values that are not returned in the results
        const availableValues: string[] = enhancedFacetValues.map(enhanced => enhanced.value);
        const missingSelectedValues: ShSelectableFacetValue[] = selectedValues.filter(
            selected => !availableValues.includes(selected.value)
        );

        const values: ShSelectableFacetValue[] = [...missingSelectedValues, ...enhancedFacetValues];
        const hasMoreValues: boolean = this.facet.hasMoreValues;

        this.facet = this.putMissingValueAtEnd({
            ...this.facet,
            hasMoreValues,
            values: this.sortFacetValues(values).map(value => ({
                ...value,
                label: this.facetTranslator.translateValue(this.FACET_FIELD, value.value, !!value.missing)
            }))
        });
    }

    private putMissingValueAtEnd(queriedFacet: ShFacet): ShFacet {
        return {
            ...queriedFacet,
            values: [
                ...queriedFacet.values.filter(value => !value.missing),
                queriedFacet.values.find(value => value.missing)
            ].filter(isNotNullOrUndefined)
        };
    }

    private sortFacetValues(facetValues: ShSelectableFacetValue[]): ShSelectableFacetValue[] {
        // Sort selected values alphabetically at the top
        const selected: ShSelectableFacetValue[] = facetValues.filter(facetValue => facetValue.selected);
        const nonSelected: ShSelectableFacetValue[] = facetValues.filter(facetValue => !facetValue.selected);

        const sortedSelected: ShSelectableFacetValue[] = selected.sort((a, b) => a.label.localeCompare(b.label));

        return [...sortedSelected, ...nonSelected];
    }
    // #endregion

    // #region Set selected
    private setSelectedFacetValues(selectedState: ShSelectableFacetValue[]): void {
        // Ensure contains only selected
        this.selectedState = selectedState.filter(facetValue => facetValue.selected);
    }

    public toggleSelectedFacetValue(toggledValue: ShSelectableFacetValue): void {
        const allSelectedState = this.updateSelectedFacets(toggledValue);

        const newState = this.filterLatestSelectedDate(allSelectedState, toggledValue);

        this.setSelectedFacetValues(newState);

        // refresh facet pages with new selected values
        this.refreshFacetPages();
    }

    private updateSelectedFacets(toggledValue: ShSelectableFacetValue): ShSelectableFacetValue[] {
        const wasSelected = !!this.selectedState.find(selectable => selectable.value === toggledValue.value);

        const newState = wasSelected
            ? // Filter out deselected value
              this.selectedState.filter(selectable => selectable.value !== toggledValue.value)
            : // Add selected value
              [...this.selectedState, toggledValue];

        // SHOULD -> Selections do not influence each other
        // MUST -> Count of every selected value must equal lowest selected count
        if (toggledValue.selectionType === FacetSelectionType.MUST) {
            if (this.COUNT_MAP.has(toggledValue.value)) {
                this.COUNT_MAP.delete(toggledValue.value);
            } else {
                this.COUNT_MAP.set(toggledValue.value, toggledValue.count ?? 1);
            }

            newState.forEach(value => (value.count = Math.min(...this.COUNT_MAP.values())));
        }
        return newState;
    }

    private filterLatestSelectedDate(
        selectedState: ShSelectableFacetValue[],
        latestSelected: ShSelectableFacetValue
    ): ShSelectableFacetValue[] {
        return selectedState.filter(
            facetValue => !(IsLaxDateRangeQuery(latestSelected.value) && facetValue.value !== latestSelected.value)
        );
    }
    // #endregion

    // #region Toggle search
    private setSearch(): void {
        this.showSearch =
            !this.facet?.values ||
            this.facet.values.length === 0 ||
            !RegExp(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{6}Z/).exec(this.facet.values[0].value);
    }
    // #endregion

    // #region Interactions
    public onSelectedSuggestion(suggestions: FormSelectOption<ShSelectableFacetValue>[]): void {
        // Clear search control
        this.facetsSearchControl.setValue(null);
        const selectableFacetValue = suggestions[0].value;
        this.toggleSelectedFacetValue(selectableFacetValue);
    }

    public onValueSelected(selectedValue: ShSelectedFacetValue): void {
        this.toggleSelectedFacetValue(
            this.toSelectableFacetValue(
                selectedValue.value,
                selectedValue.valueLabel,
                selectedValue.selectionType,
                selectedValue.count,
                selectedValue.missing
            )
        );
    }

    public onLoadMore(): void {
        this.currentPage++;

        this.loadFacetPages(this.currentPage);
    }

    public refreshFacetPages(): void {
        this.loadFacetPages(this.currentPage, true);
    }

    public submit(): void {
        const selectedValues: ShSelectedFacetValue[] = this.selectedState
            .filter(facetValue => facetValue.selected)
            .map(selectedState =>
                this.toSelectedFacetValue(
                    selectedState.value,
                    selectedState.selectionType,
                    selectedState.label,
                    selectedState.missing
                )
            );

        const output: ProfileFacetSearchDialogOutputSuccess = {
            type: 'success',
            selectedValues
        };

        this.dialog.close(output);
    }

    public cancel(): void {
        const output: ProfileFacetSearchDialogOutput = { type: 'cancel' };
        this.dialog.close(output);
    }
    // #endregion

    // #region ApiQuery helpers

    /**
     * Base StructuredQuery should be without the current selected values included
     * @param apiQuery
     * @param facetDottedKey
     * @returns
     */
    private stripFacetFromStructuredQuery(structuredQuery: StructuredQuery, facetDottedKey: string): StructuredQuery {
        const facets = structuredQuery.Facets
            ? structuredQuery.Facets.filter(facet => facet.DottedKey !== facetDottedKey)
            : [];
        const strippedStructuredQuery = {
            ...structuredQuery,
            Facets: facets
        };
        return strippedStructuredQuery;
    }

    /**
     * Add current selected values to query, but only for MUST facets
     */
    protected get apiQuery(): ApiQuery {
        const updatedStructuredQuery = cloneDeep(this.BASE_STRUCTURED_QUERY);

        const selectedValues = this.selectedState.map(selected => selected.value);

        if (selectedValues.length > 0 && this.FACET_FIELD.SelectionType === FacetSelectionType.MUST) {
            const updatedFacets = {
                DottedKey: this.FACET_FIELD.DottedKey,
                LogicalOperand: LogicalOperand.And,
                Buckets: [
                    ...selectedValues.map(value => ({
                        Operand: Operand.Include,
                        Value: value
                    }))
                ]
            };

            updatedStructuredQuery.Facets = [updatedFacets];
        }
        const apiQuery = new ApiQuery(updatedStructuredQuery, this.QUERY_RESTRICTIONS);
        return apiQuery;
    }
    // #endregion

    // #region Helpers
    private toSelectedFacetValue(
        value: string,
        selectionType: FacetSelectionType,
        label?: string,
        missing?: boolean
    ): ShSelectedFacetValue {
        return {
            facetLabel: this.FACET_FIELD.DisplayLabel,
            key: this.FACET_FIELD.DottedKey,
            value,
            selectionType,
            valueLabel: label ?? value,
            missing
        };
    }

    private toSelectableFacetValue(
        facetValue: string,
        valueLabel: string,
        selectionType: FacetSelectionType,
        count?: number,
        missing?: boolean,
        selected?: boolean
    ): ShSelectableFacetValue {
        return {
            value: facetValue,
            label: valueLabel,
            count: count ?? 1,
            missing,
            selected: selected ?? true,
            selectionType
        };
    }

    private enumValuesToSelectableFacetOptions(
        fieldDefinition: BaseFieldDefinition
    ): FormSelectOption<ShSelectableFacetValue>[] {
        const enumValues: EnumValue[] = fieldDefinition.Values ?? [];

        return enumValues
            .map(enumValue =>
                this.toSelectableFacetValue(
                    enumValue.Value.Label,
                    this.getFacetValueTranslationOrDefault(fieldDefinition.FlatKey, enumValue.Value.Label),
                    this.FACET_FIELD.SelectionType
                )
            )
            .map(facetValue => ({
                label: facetValue.label,
                value: facetValue
            }));
    }

    protected getFacetValueTranslationOrDefault(fieldKey: string, value: string): string {
        // Currently overrides in translation files always use the flat key
        const translationKey = `overrides.${DottedKeyToFlatKey(fieldKey)}.${value}`;
        const translated: string = this.translateService.instant(translationKey);
        return translated === translationKey ? value : translated;
    }
    // #endregion

    // #region Error handling
    private clearError(): void {
        this.facetError = false;
        this.autocompleteErrorMsg = '';
        this.facetsSearchControl.setErrors(null);
    }

    public setError(error: unknown): void {
        console.error('ProfileFacetSearchDialogComponent:: an error has occurred', error);
        this.facetError = true;
    }
    // #endregion
}
